import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clock, BookOpen, FileText, 
  Users, ChevronLeft, Timer
} from 'lucide-react';
import { LearningTimer, TasksList, ModuleStatusCard } from './LearningSystem';
import ModuleContentViewer from './ModuleContentViewer';
import ProgressGapsView from './ProgressGapsView';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Format seconds to human readable
const formatTimeHuman = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) {
    return `${h} Std ${m} Min`;
  }
  return `${m} Min`;
};

/**
 * PDL Learning Wrapper
 * 
 * This component wraps the ModuleContentViewer with the new learning time system.
 * It enforces:
 * 1. Sequential module unlocking
 * 2. Minimum learning time before quiz
 * 3. Task completion tracking
 * 4. Live session approval requirement
 * 
 * Supported courses: PDL Teil 1, PDL Teil 2, Wundexperte
 */
const PDLLearningWrapper = ({ 
  module, 
  courseId, 
  onBack,
  onStartQuiz, 
  getAuthHeader,
  onSpeak 
}) => {
  const [learningStatus, setLearningStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('content'); // 'content', 'tasks', 'status'
  const [timeCompleted, setTimeCompleted] = useState(false);
  const [gaps, setGaps] = useState(null);          // detailed gap-check (all previous modules)
  const [gapsLoading, setGapsLoading] = useState(false);

  // Check if this course requires the learning time system
  const COURSES_WITH_LEARNING_SYSTEM = [
    'course-pdl-weiterbildung-teil1',
    'course-pdl-weiterbildung-teil2',
    'course-weiterbildung-wundexperte',
    'course-wundexperte-84',
    'course-behandlungspflege-lg',
    'course-wundmanagement',
    'course-wundmanagement-pflegefachkraefte',
    'course-wund-rezertifizierung',
    'course-palliativpflege',
    'course-praxisanleiter',
    'course-pflegeberater',
    'course-hygienebeauftragter',
    'course-gerontopsychiatrie',
    'course-qualitaetsbeauftragter',
    'course-pflegehilfskraft',
    'course-rezertifizierung-43b',
    'course-externenpruefung-pfa',
    'course-intensivpflege-basis',
    'course-ambulanter-pflegeassistent',
  ];
  const requiresLearningSystem = COURSES_WITH_LEARNING_SYSTEM.includes(courseId);

  // Load learning status
  const loadStatus = useCallback(async () => {
    if (!requiresLearningSystem) {
      setLoading(false);
      setActiveView('content');
      return;
    }

    try {
      const res = await axios.get(
        `${API}/api/learning/status/${courseId}/${module.id}`,
        getAuthHeader()
      );
      setLearningStatus(res.data);
      setTimeCompleted(res.data.time_completed);
      
      // Auto-switch to content if already learning
      if (res.data.is_unlocked && res.data.accumulated_seconds > 0) {
        setActiveView('content');
      }
    } catch (error) {
      console.error('Error loading status:', error);
      toast.error('Fehler beim Laden des Lernstatus');
    }
    setLoading(false);
  }, [courseId, module.id, getAuthHeader, requiresLearningSystem]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Honor URL hash so Progress-Gap links like "/module/X#tasks" or "#quiz"
  // open the right tab automatically.
  useEffect(() => {
    if (!learningStatus?.is_unlocked) return;
    const h = (window.location.hash || '').replace('#', '').toLowerCase();
    if (h === 'tasks') {
      setActiveView('tasks');
      // clean the hash so refresh doesn't lock the tab
      window.history.replaceState(null, '', window.location.pathname);
    } else if (h === 'quiz') {
      // Quiz is started via parent's onStartQuiz callback; only trigger if time done
      if (timeCompleted) {
        onStartQuiz?.();
        window.history.replaceState(null, '', window.location.pathname);
      } else {
        setActiveView('status');
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, [learningStatus?.is_unlocked, timeCompleted, onStartQuiz]);

  // Fetch detailed gap info for locked modules (must be declared before any conditional return!)
  useEffect(() => {
    if (learningStatus && !learningStatus.is_unlocked && courseId && !gaps && !gapsLoading) {
      setGapsLoading(true);
      axios.get(`${API}/api/courses/${courseId}/progress-gaps`, getAuthHeader())
        .then(res => {
          const myOrder = learningStatus.module_order || 999;
          const filtered = (res.data.gaps || []).filter(g => g.module_order < myOrder);
          setGaps({ list: filtered });
        })
        .catch(() => setGaps({ list: [], error: true }))
        .finally(() => setGapsLoading(false));
    }
  }, [learningStatus, courseId, gaps, gapsLoading, getAuthHeader]);

  // Handle time completion
  const handleTimeCompleted = () => {
    setTimeCompleted(true);
    toast.success('🎉 Lernzeit erfüllt! Quiz ist jetzt freigeschaltet.');
    loadStatus();
  };

  // Handle quiz start with time check
  const handleStartQuiz = () => {
    if (requiresLearningSystem && !timeCompleted) {
      toast.error('Die Mindestlernzeit ist noch nicht erreicht.');
      return;
    }
    onStartQuiz();
  };

  // For non-PDL courses, just show the original viewer
  if (!requiresLearningSystem) {
    return (
      <ModuleContentViewer
        module={module}
        courseId={courseId}
        onStartQuiz={onStartQuiz}
        authHeader={getAuthHeader()}
        onSpeak={onSpeak}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-terra" />
      </div>
    );
  }

  // Module is locked — show comprehensive gap-check
  if (learningStatus && !learningStatus.is_unlocked) {
    return (
      <div className="max-w-3xl mx-auto p-6 sm:p-8 animate-fade-in" data-testid="locked-module-screen">
        <button
          onClick={onBack}
          className="mb-4 inline-flex items-center gap-2 text-sand-600 hover:text-sand-800 transition-colors text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          Zurück zur Kurs-Übersicht
        </button>

        <ProgressGapsView
          variant="page"
          loading={gapsLoading}
          error={Boolean(gaps?.error)}
          gaps={gaps?.list || []}
          lockedModuleOrder={learningStatus.module_order}
          lockedModuleTitle={module.title}
          onNavigateToModule={(moduleId, target) => {
            // Use hash to tell the next module screen which tab to open
            window.location.assign(`/module/${moduleId}#${target}`);
          }}
        />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Status line + Rubriken (Artikel-Kopf kommt aus ModuleView) */}
      <div className="max-w-7xl mx-auto px-4">
        <p className="text-[11px] uppercase font-semibold text-sand-500 mb-5" style={{ letterSpacing: "0.18em" }} data-testid="module-status-line">
          Lernzeit {timeCompleted ? 'erfüllt' : `${Math.round((learningStatus?.accumulated_seconds || 0) / 60)} Min`}
          <span style={{ color: '#8F650F' }}> · </span>
          Aufgaben {learningStatus?.tasks_submitted || 0} von {learningStatus?.tasks_total || 0}
          <span style={{ color: '#8F650F' }}> · </span>
          {learningStatus?.quiz_passed ? `Quiz ${learningStatus.quiz_score}%` : (timeCompleted ? 'Quiz bereit' : 'Quiz gesperrt')}
          <span style={{ color: '#8F650F' }}> · </span>
          Live-Session {learningStatus?.live_session_approved ? 'bestätigt' : 'ausstehend'}
        </p>

        <div className="flex gap-8">
          {[
            { id: 'content', label: 'Lerninhalte' },
            { id: 'tasks', label: 'Aufgaben' },
            { id: 'status', label: 'Fortschritt' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className="text-[12px] uppercase font-semibold pb-2.5 transition-colors"
              style={{
                letterSpacing: '0.2em',
                color: activeView === tab.id ? '#1B1A18' : '#A89A88',
                borderBottom: activeView === tab.id ? '2px solid #E07A5F' : '2px solid transparent',
                marginBottom: '-1px',
              }}
              data-testid={`module-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="h-px w-full" style={{ backgroundColor: '#E0D6CA' }} />
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Timer always visible when learning */}
        {activeView === 'content' && !timeCompleted && (
          <div className="mb-6">
            <LearningTimer
              moduleId={module.id}
              courseId={courseId}
              getAuthHeader={getAuthHeader}
              onTimeCompleted={handleTimeCompleted}
              requiredSeconds={(module.ue || module.ue_count) * 45 * 60}
              initialAccumulated={learningStatus?.accumulated_seconds || 0}
            />
          </div>
        )}

        {/* Content View */}
        {activeView === 'content' && (
          <ModuleContentViewer
            module={module}
            courseId={courseId}
            onStartQuiz={handleStartQuiz}
            authHeader={getAuthHeader()}
            onSpeak={onSpeak}
            quizLocked={requiresLearningSystem && !timeCompleted}
          />
        )}

        {/* Tasks View */}
        {activeView === 'tasks' && (
          <div className="max-w-3xl">
            <TasksList
              moduleId={module.id}
              getAuthHeader={getAuthHeader}
              onAllTasksCompleted={() => {
                toast.success('Alle Aufgaben erledigt!');
                loadStatus();
              }}
            />
          </div>
        )}

        {/* Status/Progress View */}
        {activeView === 'status' && learningStatus && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Progress Card */}
            <ModuleStatusCard
              status={learningStatus}
              onStartLearning={() => setActiveView('content')}
            />

            {/* Requirements Explanation */}
            <div className="py-1 pl-6" style={{ borderLeft: '2px solid #D89232' }}>
              <h3 className="font-display text-[22px] text-espresso mb-5">So schließt du das <span className="italic text-terra">Kapitel</span> ab.</h3>
              
              <div className="space-y-5">
                {[
                  { t: 'Aufgaben bearbeiten', d: 'Bearbeite die Fallbeispiele und Reflexionen — sie werden ggf. in der Live-Session besprochen.' },
                  { t: 'Wissenstest bestehen', d: 'Du brauchst mindestens 70 %. Sobald Aufgaben + Test erledigt sind, öffnet sich das nächste Kapitel.' },
                  { t: 'Lernzeit (läuft mit)', d: `Empfohlen: ${formatTimeHuman((module.ue || module.ue_count) * 45 * 60)}. Wird fürs Zertifikat dokumentiert — ist aber keine Voraussetzung.` },
                  { t: 'Live-Session (optional)', d: 'Besprich deine Aufgaben mit dem Dozenten — die Bestätigung dient als zusätzlicher Nachweis.' },
                ].map((step, si) => (
                  <div key={si} className="flex gap-4">
                    <span className="font-display text-xl text-terra leading-none flex-shrink-0 w-5 pt-0.5">{si + 1}</span>
                    <div>
                      <h4 className="font-heading font-semibold text-sand-800 text-[15px]">{step.t}</h4>
                      <p className="text-sm text-sand-600 leading-relaxed mt-0.5">{step.d}</p>
                    </div>
                  </div>
                ))}
              </div>

              {!learningStatus.is_completed && (
                <p className="mt-6 font-display italic text-[14px] text-sand-600">
                  <span style={{ color: '#8F650F' }}>✳</span> Wechsle oben zur Rubrik „Lerninhalte", um weiterzulesen.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDLLearningWrapper;
